<template>
  <div class="place-image-card">
    <label class="place-image-card__label">
      {{ $t("foto") }}
      <i
        v-if="locked"
        class="icon-check-circle place-image-card__locked"
        :title="$t('fotoTravadaAjuda')"
      ></i>
    </label>

    <input
      type="file"
      class="d-none"
      ref="fileInput"
      accept="image/png, image/jpeg"
      @change="onPickFile"
    />

    <div
      class="place-image-card__frame"
      :class="{
        'place-image-card__frame--empty': !hasImage && !loading
      }"
    >
      <!-- Carregando -->
      <div v-if="loading" class="place-image-card__state">
        <b-spinner small variant="secondary" />
        <span class="place-image-card__state-text">{{
          $t("atualizandoFoto")
        }}</span>
      </div>

      <!-- Com foto -->
      <template v-else-if="hasImage">
        <img
          :src="image.url"
          :alt="alt"
          class="place-image-card__img"
          data-html2canvas-ignore="true"
        />

        <!-- Carrossel: só na seta, nunca sozinho — a foto é do estabelecimento
             e trocar sem o agente mandar mudaria o que ele já aprovou -->
        <template v-if="canCycle">
          <button
            type="button"
            class="place-image-card__nav place-image-card__nav--prev"
            :title="$t('fotoAnterior')"
            :aria-label="$t('fotoAnterior')"
            @click.prevent="$emit('cycle', -1)"
          >
            <i class="icon-chevron-left"></i>
          </button>
          <button
            type="button"
            class="place-image-card__nav place-image-card__nav--next"
            :title="$t('proximaFoto')"
            :aria-label="$t('proximaFoto')"
            @click.prevent="$emit('cycle', 1)"
          >
            <i class="icon-chevron-right"></i>
          </button>
          <span class="place-image-card__counter">{{ counterLabel }}</span>
        </template>

        <div class="place-image-card__overlay">
          <button
            v-if="!canCycle"
            type="button"
            class="place-image-card__action"
            :title="$t('gerarFoto')"
            @click.prevent="$emit('generate')"
          >
            <i class="icon-camera"></i>
            <span>{{ $t("gerarFoto") }}</span>
          </button>
          <!-- A foto da agência pode ser escolhida antes do primeiro save: o
               formulário segura o arquivo e sobe junto do item. Já rejeitar
               grava no trail, e para isso o item precisa existir. -->
          <button
            type="button"
            class="place-image-card__action place-image-card__action--icon"
            :title="$t('enviarFoto')"
            @click.prevent="openFilePicker"
          >
            <i class="icon-upload"></i>
          </button>
          <button
            type="button"
            class="place-image-card__action place-image-card__action--icon"
            :title="$t('rejeitarFoto')"
            @click.prevent="$emit('reject')"
          >
            <i class="icon-x"></i>
          </button>
        </div>
      </template>

      <!-- Rejeitada: sem foto por decisão humana. Vale também antes do primeiro
           save — aí o formulário segura a decisão e a grava junto do item. -->
      <div v-else-if="locked" class="place-image-card__state">
        <i class="icon-eye-blocked place-image-card__state-icon"></i>
        <span class="place-image-card__state-text">{{
          $t("fotoRejeitada")
        }}</span>
        <button
          type="button"
          class="btn btn-sm btn-link text-secondary mt-1"
          @click.prevent="$emit('unlock')"
        >
          {{ $t("reativarBuscaAutomatica") }}
        </button>
      </div>

      <!-- Sem foto: lugar sem place_id ou sem foto no Google -->
      <div v-else class="place-image-card__state">
        <i class="icon-picture place-image-card__state-icon"></i>
        <span class="place-image-card__state-text">{{ $t("semFoto") }}</span>
        <span class="place-image-card__state-hint">{{
          $t("fotoDoLugarAviso")
        }}</span>
        <div class="mt-2">
          <button
            type="button"
            class="btn btn-sm btn-outline-primary rounded-pill"
            :disabled="!canPreview"
            :title="canPreview ? $t('gerarFoto') : $t('fotoDoLugarAviso')"
            @click.prevent="$emit('generate')"
          >
            <i class="icon-camera mr-1"></i>{{ $t("gerarFoto") }}
          </button>
          <button
            type="button"
            class="btn btn-sm btn-link text-secondary"
            @click.prevent="openFilePicker"
          >
            {{ $t("enviarFoto") }}
          </button>
        </div>
      </div>
    </div>

    <!-- De qual lugar é a foto, quando ele veio de uma busca pelo nome digitado
         em vez de um clique na sugestão: é como o agente percebe um match
         distante (a filial de outra cidade, o homônimo). -->
    <small
      v-if="hasImage && matchedName"
      class="place-image-card__match"
      :title="matchedName"
      >{{ $t("fotoDe", { place: matchedName }) }}</small
    >

    <!-- Crédito da foto: "Foto: Google" ao vivo, ou a atribuição do upload -->
    <small v-if="attribution" class="place-image-card__credit">
      <a
        v-if="image.source_url"
        :href="image.source_url"
        target="_blank"
        rel="noopener noreferrer"
        :title="license"
        >{{ attribution }}</a
      >
      <span v-else :title="license">{{ attribution }}</span>
    </small>
  </div>
</template>

<script>
export default {
  props: {
    // PlaceImage do backend: { url, attribution_string, source_url, ... }
    image: {
      type: Object,
      default: null
    },
    // place_image_locked: decisão humana, a automação não mexe mais
    locked: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    // Item já salvo — sem id não há o que resolver no backend
    ready: {
      type: Boolean,
      default: true
    },
    // Quantas fotos o Google tem para o lugar, e qual está em exibição
    count: {
      type: Number,
      default: 0
    },
    index: {
      type: Number,
      default: 0
    },
    // Há um lugar escolhido: dá para ver a foto mesmo antes do primeiro save
    canPreview: {
      type: Boolean,
      default: false
    },
    alt: {
      type: String,
      default: ""
    },
    // Lugar que a busca por texto casou — vazio quando o agente escolheu o
    // lugar no autocomplete e já sabe de quem é a foto.
    matchedName: {
      type: String,
      default: ""
    }
  },
  computed: {
    hasImage() {
      return !!(this.image && this.image.url);
    },
    canCycle() {
      return this.count > 1;
    },
    // Sem a contagem o agente não sabe se vale continuar clicando.
    counterLabel() {
      return `${this.index + 1}/${this.count}`;
    },
    attribution() {
      return this.hasImage ? this.image.attribution_string : "";
    },
    license() {
      return this.hasImage ? this.image.license : "";
    }
  },
  methods: {
    openFilePicker() {
      this.$refs.fileInput.click();
    },
    onPickFile(event) {
      const file = event.target.files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = () => this.$emit("upload", reader.result);
      reader.readAsDataURL(file);
      event.target.value = "";
    }
  }
};
</script>

<style lang="scss" scoped>
.place-image-card {
  &__label {
    display: block;
    margin-bottom: 4px;
    font-size: 0.75rem;
    color: #9b9b9b;
  }

  &__locked {
    margin-left: 3px;
    color: #b0b0b0;
  }

  &__frame {
    position: relative;
    width: 100%;
    // Altura fixa para acompanhar a coluna de campos ao lado.
    height: 180px;
    border-radius: 8px;
    overflow: hidden;
    background: #f4f4f4;

    &--empty {
      background: #fafafa;
      border: 1px dashed #d8d8d8;
    }

  }

  &__img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &__state {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 8px;
    text-align: center;
    color: #b0b0b0;
  }

  &__state-icon {
    font-size: 1.75rem;
    margin-bottom: 6px;
  }

  &__state-text {
    font-size: 0.8rem;
    margin-top: 6px;
  }

  &__state-hint {
    font-size: 0.7rem;
    margin-top: 4px;
    line-height: 1.3;
  }

  // Setas sempre visíveis (não só no hover): são a única pista de que o lugar
  // tem mais de uma foto.
  &__nav {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    padding: 0;
    border: none;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.45);
    color: #fff;
    font-size: 0.9rem;
    line-height: 1;

    &:hover,
    &:focus {
      background: rgba(0, 0, 0, 0.7);
    }

    &--prev {
      left: 6px;
    }

    &--next {
      right: 6px;
    }
  }

  &__counter {
    position: absolute;
    z-index: 2;
    left: 50%;
    bottom: 8px;
    transform: translateX(-50%);
    padding: 1px 8px;
    border-radius: 10px;
    background: rgba(0, 0, 0, 0.45);
    color: #fff;
    font-size: 0.7rem;
    line-height: 1.5;
  }

  &__overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: flex-end;
    justify-content: flex-end;
    padding: 8px;
    opacity: 0;
    background: linear-gradient(
      to top,
      rgba(0, 0, 0, 0.55) 0%,
      rgba(0, 0, 0, 0) 60%
    );
    transition: opacity 150ms ease;
  }

  &__frame:hover &__overlay,
  &__overlay:focus-within {
    opacity: 1;
  }

  &__action {
    display: inline-flex;
    align-items: center;
    padding: 4px 10px;
    margin-left: 6px;
    border: none;
    border-radius: 20px;
    background: rgba(255, 255, 255, 0.92);
    color: #311b41;
    font-size: 0.75rem;
    font-weight: 500;
    line-height: 1.2;

    i {
      margin-right: 5px;
    }

    &:hover {
      background: #fff;
    }

    &--icon {
      padding: 4px 8px;

      i {
        margin-right: 0;
      }
    }
  }

  &__match {
    display: block;
    margin-top: 4px;
    color: #9b9b9b;
    font-size: 0.7rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__credit {
    display: block;
    margin-top: 4px;
    color: #b0b0b0;
    font-size: 0.7rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__credit a {
    color: #b0b0b0;
    text-decoration: underline;
  }
}
</style>
